import React, { useEffect, useState, useRef, useMemo } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
} from "chart.js";

import ToolTip from "./ToolTip";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
);

const toNumberOrNull = (v) =>
  v === null || v === undefined || v === "" ? null : Number(v);
const parseLabelDate = (label) => {
  if (!label) return null;
  if (label instanceof Date) return label;

  const full = String(label).trim();
  // Split into date + optional time so we can sort to the second.
  // e.g. "05-04-2026 08:45:26" -> datePart "05-04-2026", timePart "08:45:26"
  const [datePart, timePart] = full.split(/\s+/);
  const s = datePart || full;

  // Parse HH:mm:ss (time defaults to 00:00:00 when absent)
  let hh = 0,
    mm = 0,
    ss = 0;
  if (timePart) {
    const tp = timePart.split(":").map((x) => Number(x));
    hh = Number.isFinite(tp[0]) ? tp[0] : 0;
    mm = Number.isFinite(tp[1]) ? tp[1] : 0;
    ss = Number.isFinite(tp[2]) ? tp[2] : 0;
  }

  // If browser can parse the full string directly (e.g. ISO 2026-01-16T..)
  const direct = new Date(full);
  if (!isNaN(direct.getTime())) return direct;

  // Slash format: MM/DD/YYYY or DD/MM/YYYY
  if (s.includes("/")) {
    const [a, b, c] = s.split("/").map((x) => Number(x));
    if (!a || !b || !c) return null;

    // If first part > 12 => DD/MM/YYYY else MM/DD/YYYY
    const dt =
      a > 12
        ? new Date(c, b - 1, a, hh, mm, ss)
        : new Date(c, a - 1, b, hh, mm, ss);
    return isNaN(dt.getTime()) ? null : dt;
  }

  // Dash format: MM-DD-YYYY or DD-MM-YYYY or YYYY-MM-DD
  if (s.includes("-")) {
    const parts = s.split("-").map((p) => p.trim());
    if (parts.length !== 3) return null;

    const [p1, p2, p3] = parts;

    // YYYY-MM-DD
    if (p1.length === 4) {
      const y = Number(p1),
        m = Number(p2),
        d = Number(p3);
      const dt = new Date(y, m - 1, d, hh, mm, ss);
      return isNaN(dt.getTime()) ? null : dt;
    }

    // MM-DD-YYYY or DD-MM-YYYY
    if (p3.length === 4) {
      const a = Number(p1),
        b = Number(p2),
        y = Number(p3);
      if (!a || !b || !y) return null;

      // If first part > 12 => DD-MM-YYYY else MM-DD-YYYY (your case is MM-DD-YYYY)
      const dt =
        a > 12
          ? new Date(y, b - 1, a, hh, mm, ss)
          : new Date(y, a - 1, b, hh, mm, ss);
      return isNaN(dt.getTime()) ? null : dt;
    }
  }

  return null;
};

/** Filter labels+data by range */
const filterDataByMonth = (labels = [], data = [], monthType = "Monthly") => {
  if (!Array.isArray(labels) || !Array.isArray(data) || labels.length === 0) {
    return { filteredLabels: [], filteredData: [] };
  }

  // no filtering — still sort ascending so latest sits rightmost
  if (!monthType || monthType === "All") {
    const indexed = labels
      .map((label, i) => ({ label, i, dt: parseLabelDate(label) }))
      .sort((a, b) => {
        const aT = a.dt ? a.dt.getTime() : 0;
        const bT = b.dt ? b.dt.getTime() : 0;
        return aT - bT;
      });
    return {
      filteredLabels: indexed.map((x) => x.label),
      filteredData: indexed.map((x) => {
        const v = data[x.i];
        return v === null || v === undefined || v === "" ? null : Number(v);
      }),
    };
  }

  const now = new Date();
  let daysBack = 30;

  switch (monthType) {
    case "Daily":
      daysBack = 1;
      break;
    case "Weekly":
      daysBack = 7;
      break;
    case "Monthly":
      daysBack = 30;
      break;
    case "3 Months":
      daysBack = 90;
      break;
    case "6 Months":
      daysBack = 180;
      break;
    case "Yearly":
      daysBack = 365;
      break;
    default:
      daysBack = 30;
  }

  const filterDate = new Date(now.getTime() - daysBack * 24 * 60 * 60 * 1000);

  const filteredLabels = [];
  const filteredData = [];

// Collect, then sort ascending by parsed date so the latest point is rightmost.
const matched = [];
labels.forEach((label, i) => {
  const dt = parseLabelDate(label);
  if (dt && dt >= filterDate) {
    matched.push({ label, dt, i });
  }
});
matched.sort((a, b) => a.dt - b.dt);
matched.forEach(({ label, i }) => {
  filteredLabels.push(label);
  const v = data[i];
  filteredData.push(v === null || v === undefined || v === "" ? null : Number(v));
});


  // fallback: if nothing matched, show last up to 10 valid points by date
  if (filteredLabels.length === 0) {
    const indexed = labels
      .map((label, i) => ({ label, i, dt: parseLabelDate(label) }))
      .filter((x) => x.dt && !isNaN(x.dt.getTime()))
      .sort((a, b) => b.dt - a.dt)
      .slice(0, Math.min(10, labels.length))
      .sort((a, b) => a.dt - b.dt);

indexed.forEach((x) => {
  filteredLabels.push(x.label);
  const v = data[x.i];
  filteredData.push(v === null || v === undefined || v === "" ? null : Number(v));
});

  }

  return { filteredLabels, filteredData };
};

/** Draw value labels above points (like your screenshot) */
const valueLabelPlugin = {
  id: "valueLabelPlugin",
  afterDatasetsDraw(chart) {
    const { ctx } = chart;

    chart.data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);
      if (meta.hidden) return;

      meta.data.forEach((point, i) => {
        const val = dataset.data?.[i];
        if (val === null || val === undefined || val === "") return;

        ctx.save();
        ctx.font = "700 12px Arial";
        ctx.fillStyle = dataset.borderColor || "#111";
        ctx.textAlign = "center";
        ctx.textBaseline = "bottom";
        ctx.fillText(String(val), point.x, point.y - 12);
        ctx.restore();
      });
    });
  },
};

function LineChart({
  selectedTab,
  graphSwitch,
  resdata,
  selectedMonth = "Monthly",
}) {
  const [tooltipModel, setTooltipModel] = useState({});
  const [showTooltip, setShowTooltip] = useState(false);
  const [position, setPosition] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const menuRef = useRef();
  const chartRef = useRef();

  // dark
  const rawdata1 = useMemo(
    () => ({
      fill: false,
      tension: 0,
      cubicInterpolationMode: "default",
      borderColor: "#1D2951",
      backgroundColor: "#1D2951",
      borderWidth: 2, // ✅ 2px line
      pointBackgroundColor: "#1D2951",
      pointBorderColor: "#ffffff",
      pointBorderWidth: 2,
      pointRadius: 5,
      pointHoverRadius: 6,
    }),
    [],
  );

  // orange
  const rawdata2 = useMemo(
    () => ({
      fill: false,
      tension: 0,
      cubicInterpolationMode: "default",
      borderColor: "#FB923C",
      backgroundColor: "#FB923C",
      borderWidth: 2, // ✅ 2px line
      pointBackgroundColor: "#FB923C",
      pointBorderColor: "#ffffff",
      pointBorderWidth: 2,
      pointRadius: 5,
      pointHoverRadius: 6,
    }),
    [],
  );

  const [renderdata, setRenderData] = useState({ labels: [], datasets: [] });

  useEffect(() => {
    const updatedData = { labels: [], datasets: [] };

    // BP
    if (selectedTab === 0) {
      const labels =
        resdata?.diastolicdatelabel || resdata?.systolicdatelabel || [];
      const systolicArr = resdata?.systolic || [];
      const diastolicArr = resdata?.diastolic || [];

      const { filteredLabels, filteredData: filteredSystolic } =
        filterDataByMonth(labels, systolicArr, selectedMonth);

      // IMPORTANT: use the SAME filteredLabels for diastolic indexing
      // We rebuild diastolic by mapping filteredLabels back to original index
      const labelToIndex = new Map(labels.map((l, i) => [l, i]));
      const filteredDiastolic = filteredLabels.map((l) => {
  const idx = labelToIndex.get(l);
  return toNumberOrNull(diastolicArr[idx]);
});


      updatedData.labels = filteredLabels;

      if (graphSwitch?.Systolic) {
        updatedData.datasets.push({
          ...rawdata2,
          spanGaps: false,
          data: filteredSystolic,
          label: "Systolic",
        });
      }
      if (graphSwitch?.Diastolic) {
        updatedData.datasets.push({
          ...rawdata1,
          spanGaps: false,
          data: filteredDiastolic,
          label: "Diastolic",
        });
      }
    }

    // Weight/BMI
//     if (selectedTab === 1) {
//       const labels = resdata?.wtdatelabel || [];
//       const weightArr = resdata?.weight || [];
//       const bmiArr = resdata?.bmi || [];

//       const { filteredLabels, filteredData: filteredWeight } =
//         filterDataByMonth(labels, weightArr, selectedMonth);

//       const labelToIndex = new Map(labels.map((l, i) => [l, i]));
//       const filteredBMI = filteredLabels.map((l) => {
//   const idx = labelToIndex.get(l);
//   return toNumberOrNull(bmiArr[idx]);
// });


//       updatedData.labels = filteredLabels;
//       if (graphSwitch?.Weight)
//         updatedData.datasets.push({
//           ...rawdata2,
//           spanGaps: false,
//           data: filteredWeight,
//           label: "Weight",
//         });
//       if (graphSwitch?.BMI)
//         updatedData.datasets.push({
//           ...rawdata1,
//           spanGaps: false,
//           data: filteredBMI,
//           label: "BMI",
//         });
//     }
if (selectedTab === 2) {
  const wtLabels = resdata?.wtdatelabel || [];
  const weightArr = resdata?.weight || [];

  const bmiLabels = resdata?.bmidatelabel || [];
  const bmiArr = resdata?.bmi || [];

  // Filter weight
  const {
    filteredLabels: filteredWtLabels,
    filteredData: filteredWeight,
  } = filterDataByMonth(wtLabels, weightArr, selectedMonth);

  // Filter BMI separately
  const {
    filteredLabels: filteredBmiLabels,
    filteredData: filteredBMIData,
  } = filterDataByMonth(bmiLabels, bmiArr, selectedMonth);

  // Create unified label set (sorted by date)
  const allLabels = Array.from(
    new Set([...filteredWtLabels, ...filteredBmiLabels])
  ).sort((a, b) => parseLabelDate(a) - parseLabelDate(b));

  // Map values to unified labels
  const wtMap = new Map(filteredWtLabels.map((l, i) => [l, filteredWeight[i]]));
  const bmiMap = new Map(
    filteredBmiLabels.map((l, i) => [l, filteredBMIData[i]])
  );

  const alignedWeight = allLabels.map((l) =>
    wtMap.has(l) ? wtMap.get(l) : null
  );

  const alignedBMI = allLabels.map((l) =>
    bmiMap.has(l) ? bmiMap.get(l) : null
  );

  updatedData.labels = allLabels;

  if (graphSwitch?.Weight) {
    updatedData.datasets.push({
      ...rawdata2,
      spanGaps: !false,
      data: alignedWeight,
      label: "Weight",
    });
  }

  if (graphSwitch?.BMI) {
    updatedData.datasets.push({
      ...rawdata1,
      spanGaps: !false,
      data: alignedBMI,
      label: "BMI",
    });
  }
}

    // Pulse
    if (selectedTab === 1) {
      const labels = resdata?.pulsedatelabel || [];
      const pulseArr = resdata?.pulse || [];

      const { filteredLabels, filteredData: filteredPulse } = filterDataByMonth(
        labels,
        pulseArr,
        selectedMonth,
      );

      updatedData.labels = filteredLabels;
      if (graphSwitch?.Pulse)
        updatedData.datasets.push({
          ...rawdata2,
          data: filteredPulse,
          label: "Pulse",
        });
    }

    // O2
    if (selectedTab === 3) {
      const labels = resdata?.O2Satdatelabel || [];
      const o2Arr = resdata?.O2Sat || [];

      const { filteredLabels, filteredData: filteredO2 } = filterDataByMonth(
        labels,
        o2Arr,
        selectedMonth,
      );

      updatedData.labels = filteredLabels;
      if (graphSwitch?.O2Saturation)
        updatedData.datasets.push({
          ...rawdata2,
          data: filteredO2,
          label: "O2 Saturation",
        });
    }

    setRenderData(updatedData);
  }, [selectedTab, graphSwitch, resdata, selectedMonth, rawdata1, rawdata2]);

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: {
          left: 40,
          right: 10,
          top: 30,
          bottom: 10,
        },
      },
      scales: {
        y: {
          grid: { display: true, drawTicks: false },
          border: { display: false },
          ticks: { stepSize: 20 },
        },
        x: {
          offset: true,
          grid: { display: false },
          border: { display: false },
          ticks: {
            callback: function (value) {
              const raw = this.getLabelForValue
                ? this.getLabelForValue(value)
                : value;
              const str = String(raw);
              // Show only the date part if a time is appended
              return str.includes(" ") ? str.split(" ")[0] : str;
            },
          },
        },
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: false,
          mode: "index",
          intersect: false,
          external: (context) => {
            const tooltip = context.tooltip;
            if (tooltip.opacity === 0) {
              setShowTooltip(false);
              return;
            }
            setTooltipModel(tooltip);
            setPosition(context.chart.canvas.getBoundingClientRect());
            setShowTooltip(true);
          },
        },
      },
    }),
    [],
  );

  // Download handlers
  const handleDownload = (type) => {
    if (!chartRef.current) return;

    const canvas = chartRef.current.canvas;

    // white background export
    const tempCanvas = document.createElement("canvas");
    tempCanvas.width = canvas.width;
    tempCanvas.height = canvas.height;
    const ctx = tempCanvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(canvas, 0, 0);

    const link = document.createElement("a");
    link.download = `chart.${type}`;
    link.href = tempCanvas.toDataURL(`image/${type}`);
    link.click();
    setModalOpen(false);
  };

  const handlePDFDownload = () => {
    if (!chartRef.current) return;
    const canvas = chartRef.current.canvas;

    const pdf = new jsPDF({
      orientation: canvas.width > canvas.height ? "landscape" : "portrait",
      unit: "pt",
      format: [canvas.width, canvas.height],
    });

    pdf.addImage(
      canvas.toDataURL("image/png"),
      "PNG",
      0,
      0,
      canvas.width,
      canvas.height,
    );
    pdf.save("chart.pdf");
    setModalOpen(false);
  };

  const handleSVGDownload = () => {
    if (!chartRef.current) return;
    const canvas = chartRef.current.canvas;
    const imgData = canvas.toDataURL("image/png");

    const svgContent = `<svg xmlns="http://www.w3.org/2000/svg" width="${canvas.width}" height="${canvas.height}">
      <image href="${imgData}" height="${canvas.height}" width="${canvas.width}" />
    </svg>`;

    const blob = new Blob([svgContent], {
      type: "image/svg+xml;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = "chart.svg";
    link.click();
    setModalOpen(false);
  };

  const handleCSVDownload = () => {
    const header = ["Label", ...renderdata.datasets.map((ds) => ds.label)].join(
      ",",
    );
    const rows = renderdata.labels.map((label, i) => {
      const values = renderdata.datasets.map((ds) => ds.data?.[i] ?? "");
      return [label, ...values].join(",");
    });

    const csv = [header, ...rows].join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "chart-data.csv";
    link.click();
    setModalOpen(false);
  };

  const handleXLSDownload = () => {
    const wsData = [
      ["Label", ...renderdata.datasets.map((ds) => ds.label)],
      ...renderdata.labels.map((label, i) => [
        label,
        ...renderdata.datasets.map((ds) => ds.data?.[i] ?? ""),
      ]),
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(wsData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Chart Data");
    XLSX.writeFile(workbook, "chart-data.xlsx");
    setModalOpen(false);
  };

  return (
    <div style={{ width: "100%", height: "100%", position: "relative" }}>
      <div className="flex justify-end px-4 pt-2" ref={menuRef}>
        <button
          className="text-gray-600 hover:text-black"
          onClick={() => setModalOpen(true)}
        >
          ☰
        </button>
      </div>

      <Line
        ref={chartRef}
        options={options}
        data={renderdata}
        plugins={[valueLabelPlugin]}
      />

      {tooltipModel?.dataPoints &&
        tooltipModel.dataPoints.map((tooltip, index) => (
          <ToolTip
            key={index}
            data={tooltip}
            isShow={showTooltip}
            position={position}
            color={tooltipModel?.labelColors?.[index]?.borderColor}
          />
        ))}

      {modalOpen && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center">
          <div className="bg-white text-sm rounded shadow-lg w-[90%] max-w-[512px]">
            <div className="flex justify-end px-2 pt-2">
              <button
                className="text-gray-600 hover:text-black text-lg font-bold"
                onClick={() => setModalOpen(false)}
              >
                ×
              </button>
            </div>

            <div className="flex flex-wrap justify-start px-4 py-2 gap-4 text-blue-600">
              <button
                onClick={() => handleDownload("png")}
                className="hover:underline"
              >
                Download PNG
              </button>
              <button
                onClick={() => handleDownload("jpeg")}
                className="hover:underline"
              >
                Download JPEG
              </button>
              <button onClick={handlePDFDownload} className="hover:underline">
                Download PDF
              </button>
              <button onClick={handleSVGDownload} className="hover:underline">
                Download SVG
              </button>
              <button onClick={handleCSVDownload} className="hover:underline">
                Download CSV
              </button>
              <button onClick={handleXLSDownload} className="hover:underline">
                Download XLS
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LineChart;
